import { dateText } from './logic';
import type { Followup, PatientContext, PriorReport, Reception } from './shapes';
import { busy, button, el, loading, type RadiologistDeps, textBlock, toolCard, toolFailure, traceNodes } from './ui';

/**
 * "Contexto del paciente" (contract v3 §2.4): shown once per study when the Asistente opens on it —
 * what reception recorded, the patient's signed priors with an excerpt of their impression, and the
 * radiology follow-ups still pending. Data only (no model, no cost); "Resumir" asks the model for a
 * short summary of indication and priors.
 */

export const CONTEXT_TEXT = {
  tool: 'Contexto del paciente',
  title: 'Contexto del paciente',
  loading: 'cargando el contexto…',
  reception: 'Recepción',
  diagnosis: 'Diagnóstico',
  referring: 'Médico remitente',
  note: 'Nota',
  priority: 'Prioridad',
  critical: 'Marcado como crítico',
  procedures: 'Procedimientos',
  priors: 'Informes previos',
  noPriors: 'Sin informes previos firmados.',
  followups: 'Seguimientos',
  noFollowups: 'Sin seguimientos pendientes.',
  due: 'vence',
  summarise: 'Resumir',
  summarising: 'Resumiendo…',
  summary: 'Resumen sugerido:',
  failed: 'No fue posible cargar el contexto del paciente.',
  summaryFailed: 'No fue posible resumir el contexto.',
} as const;

const FOLLOWUP_STATUS: Record<Followup['status'], string> = { pending: 'pendiente', done: 'realizado', cancelled: 'cancelado' };

function field(label: string, value: string | null): HTMLElement | null {
  if (!value) return null;
  const row = el('div', 'assistant-context-field');
  row.append(el('span', 'assistant-context-label', `${label}: `), el('span', 'assistant-context-value', value));
  return row;
}

function section(title: string, kind: string): HTMLElement {
  const box = el('div', `assistant-context-section context-${kind}`);
  box.appendChild(el('div', 'assistant-report-section-title', title));
  return box;
}

function receptionBlock(reception: Reception | null): HTMLElement | null {
  if (!reception) return null;
  const box = section(CONTEXT_TEXT.reception, 'reception');
  if (reception.critical) box.appendChild(el('span', 'assistant-severity severity-critical', CONTEXT_TEXT.critical));
  const rows = [
    field(CONTEXT_TEXT.diagnosis, reception.diagnosis), field(CONTEXT_TEXT.referring, reception.referringDoctor),
    field(CONTEXT_TEXT.note, reception.note), field(CONTEXT_TEXT.priority, reception.priority),
  ].filter((n): n is HTMLElement => n !== null);
  box.append(...rows);
  return rows.length > 0 || reception.critical ? box : null;
}

function priorItem(prior: PriorReport): HTMLElement {
  const item = el('li', 'assistant-context-prior');
  const head = [dateText(prior.signedAt), prior.accession, prior.procedures.join(', ')].filter((p) => p && p !== '—').join(' · ');
  if (head) item.appendChild(el('div', 'assistant-context-prior-head', head));
  if (prior.impression) item.appendChild(el('div', 'assistant-context-prior-text', prior.impression));
  return item;
}

function followupItem(f: Followup): HTMLElement {
  const parts = [`${CONTEXT_TEXT.due} ${dateText(f.dueOn)}`, f.modality, f.reason, f.guideline ? `(${f.guideline})` : null];
  if (f.status !== 'pending') parts.push(FOLLOWUP_STATUS[f.status]);
  const item = el('li', `assistant-context-followup followup-${f.status}`, parts.filter(Boolean).join(' · '));
  item.dataset.status = f.status;
  return item;
}

function listBlock<T>(title: string, kind: string, items: T[], render: (item: T) => HTMLElement, empty: string): HTMLElement {
  const box = section(title, kind);
  if (items.length === 0) {
    box.appendChild(el('p', 'assistant-tool-note', empty));
    return box;
  }
  const list = el('ul', 'assistant-context-list');
  items.forEach((item) => list.appendChild(render(item)));
  box.appendChild(list);
  return box;
}

function renderContext(deps: RadiologistDeps, study: string, context: PatientContext, body: HTMLElement): void {
  const blocks: HTMLElement[] = [];
  const reception = receptionBlock(context.reception);
  if (reception) blocks.push(reception);
  if (context.procedures.length > 0) {
    blocks.push(listBlock(CONTEXT_TEXT.procedures, 'procedures', context.procedures,
      (p) => el('li', 'assistant-context-procedure', [p.code, p.name, p.modality].filter(Boolean).join(' · ')), ''));
  }
  blocks.push(listBlock(CONTEXT_TEXT.priors, 'priors', context.priors, priorItem, CONTEXT_TEXT.noPriors));
  blocks.push(listBlock(CONTEXT_TEXT.followups, 'followups', context.followups, followupItem, CONTEXT_TEXT.noFollowups));
  const summary = el('div', 'assistant-context-summary');
  summary.setAttribute('aria-live', 'polite');
  const run = button(CONTEXT_TEXT.summarise, 'assistant-context-summarise', () => void busy(run, CONTEXT_TEXT.summarising, async () => {
    try {
      const out = await deps.api.contextSummary(study);
      summary.replaceChildren(textBlock(CONTEXT_TEXT.summary, out.text), ...traceNodes(out));
    } catch (error) {
      console.warn('[viewer] context summary failed', error);
      deps.shell.toast(toolFailure(error, 'patient_context', CONTEXT_TEXT.summaryFailed), 'error');
    }
  }));
  const actions = el('div', 'assistant-finding-actions');
  actions.appendChild(run);
  body.replaceChildren(...blocks, actions, summary);
}

/** Loads and shows the context card for `study` (replacing an earlier one of the same study). */
export async function showPatientContext(deps: RadiologistDeps, study: string): Promise<void> {
  const { body } = toolCard(deps, 'patient-context', CONTEXT_TEXT.title, study);
  loading(body, CONTEXT_TEXT.loading);
  try {
    renderContext(deps, study, await deps.api.patientContext(study), body);
  } catch (error) {
    console.warn('[viewer] patient context failed', error);
    body.replaceChildren(el('p', 'assistant-tool-note assistant-tool-error', toolFailure(error, 'patient_context', CONTEXT_TEXT.failed)));
  }
}

const shown = new Set<string>();

/** The Asistente opened a study's conversation: show its context the first time only. */
export function contextOnOpen(deps: RadiologistDeps, study: string): void {
  if (shown.has(study)) return;
  shown.add(study);
  void showPatientContext(deps, study);
}
